import values from 'lodash/values';
import findIndex from 'lodash/findIndex';
import shuffle from 'lodash/shuffle';
import sortBy from 'lodash/sortBy';
import _includes from 'lodash/includes';
import utils from '@/utils/auth';
import { ExerciseResponse, ExercisesHWReviewElement } from '@/interfaces/class/exercises';
import { Lesson } from '@/interfaces/class/lesson';
import { BookState } from '@/interfaces/class/state';
import { Training, TrainingResult } from '@/interfaces/class/training';
import {
  CHECK,
  CHECK_CLICK,
  CHECK_HW,
  CHECK_PUZZLE,
  CHECK_T_PART,
} from '@/action-types/button';

const checkIsNeedReset = (btnType: string) => {
  const resetBtns = [CHECK, CHECK_HW, CHECK_T_PART, CHECK_CLICK, CHECK_PUZZLE];

  return _includes(resetBtns, btnType);
};

const findLessonId = (state: any) => {
  const lessons = values(state.book.lesson?.lessons || {});

  const currentId = parseInt(state.book.route.lessonId || '0', 10);
  const currentIndex = findIndex(
    lessons[0],
    ((lesson: { id: number; }) => currentId === lesson.id),
  );

  if (lessons[0]) {
    return (lessons[0][currentIndex - 1] || {}).id;
  }

  return undefined;
};

const checkIsCanSave = (state: BookState) => {
  const exerciseType = state.exercise?.currentExercise?.activity || 'class';
  const isOnlineStudent = state.sync?.student?.isConnected || false;
  const isOnlineTeacher = state.sync?.teacher?.isConnected || false;

  if ((state.route?.sessionId || '').match('0-')) return false;
  if (exerciseType === 'class' && (!isOnlineStudent || !isOnlineTeacher)) return false;
  if (exerciseType === 'home' && utils.parseToken(utils.getAccessToken()).role === 'teacher') return false;

  return true;
};

const checkIsNeedHWReview = (state: BookState) => {
  const alias = state.exercise.currentExercise?.alias || '';
  const data = state.exercise.currentExercise?.data || [];

  return alias === 'homework-review' && !data.length;
};

const getHomeworkIds = (state: any) => {
  const exercises = (state.data.getExercises || []).filter((el: {
    activity: string;
    priority: number;
    required: boolean;
  }) => el.activity === 'home');

  const compare = (data: any) => sortBy(data, (exercise: {
    priority: number;
  }) => exercise.priority);
  const sortedExercises = compare(exercises);

  const aliases : {
    [key: string]: {
      exerciseProgress: [];
      title: string;
      required: boolean;
    };
  } = {};

  sortedExercises.forEach((el: ExerciseResponse) => {
    // @ts-ignore
    aliases[`${el.id}`] = { ...el };
  });

  return {
    ids: sortedExercises.map((el: ExerciseResponse) => el.id),
    aliases,
  };
};

const setHomeworkElements = (
  hwExercises: {
    [key: string]: {
      id?: number;
      elements?: any[];
      exerciseProgress: any[];
      title: string;
      required: boolean;
    };
  },
  data: {
    [key: string]: Training;
  },
  state: any,
) => {
  const { currentExercise } = state.book.exercise;
  const elements : {
    id: number;
    title: string;
    exerciseProgress: any[];
    required: boolean;
    elements: {
      id: number;
      elementAnswers: {
        id: number;
        answer: string;
        isCorrect: boolean;
      }[];
    };
  }[] = [];

  Object.keys(data).forEach((key: string) => {
    const keyData = key.split('ex_');

    elements.push({
      id: parseInt(keyData[1], 10),
      // @ts-ignore
      elements: data[key],
      ...hwExercises[keyData[1]],
    });

    if (!currentExercise.currentScreen && hwExercises[keyData[1]].required) {
      currentExercise.currentScreen = hwExercises[keyData[1]].title;
    }
  });

  // @ts-ignore
  currentExercise.data = elements;

  return currentExercise;
};

const convertTrainingResult = (
  result: TrainingResult,
  state: BookState,
  btnType: string,
) => {
  const answers = state.exercise?.currentExercise?.answers || {};
  const currentSlide = state.exercise?.currentExercise?.currentSlide;
  const dataTraining = state.exercise?.currentExercise?.dataTraining || [];

  let lastTraining = state.exercise?.currentExercise?.lastTraining || '';

  if (dataTraining[currentSlide]) {
    const sessionElement = `/api/course/session/course_sessions/${state.route?.sessionId}`;
    const elementAnswers = dataTraining[currentSlide].elementAnswers || [];
    const previousAnswer = findIndex(
      elementAnswers,
      ((el: TrainingResult) => el.trainingType === result.trainingType),
    );

    const resultElement = {
      courseElement: `/api/course/elements/${dataTraining[currentSlide].id}`,
      session: sessionElement,
      ...result,
    };
    let resultAnswer = {
      ...resultElement,
      // @ts-ignore
      isCorrect: answers.isCorrect !== undefined && !answers.isCorrect ? false : result.isCorrect,
    };

    dataTraining[currentSlide].elementAnswers = [...dataTraining[currentSlide].elementAnswers];

    if (previousAnswer !== -1) {
      const answer = dataTraining[currentSlide].elementAnswers[previousAnswer];

      resultElement.isCorrect = answer.isCorrect ? answer.isCorrect : resultAnswer.isCorrect;
      dataTraining[currentSlide].elementAnswers[previousAnswer] = {
        ...answer,
        ...resultElement,
        isCorrect: resultAnswer.isCorrect,
      };
      resultAnswer = {
        ...answer,
        ...resultAnswer,
        ...resultElement,
      };
    } else {
      // @ts-ignore
      dataTraining[currentSlide].elementAnswers.push(resultElement);
    }

    if (resultElement.isCorrect) {
      if (dataTraining[currentSlide].allowedTrainingTypes.length) {
        lastTraining = dataTraining[currentSlide].allowedTrainingTypes?.[0];
      }

      if (resultElement.trainingType === 'DontKnow') {
        lastTraining = 'DontKnow';
      }
    }

    ['lastIndex', 'userAnswer', 'isTeacher'].forEach((el) => {
      delete resultElement[el];
    });

    return {
      id: dataTraining[currentSlide]?.elementAnswers?.[previousAnswer]?.id || null,
      resultElement,
      state: {
        ...state,
        exercise: {
          ...state.exercise,
          currentExercise: {
            ...state.exercise.currentExercise,
            answers: resultAnswer,
            dataTraining,
            lastTraining,
            isFinishedTraining: btnType === 'CHECK',
          },
        },
      },
    };
  }

  return null;
};

const convertHWResult = (
  result: TrainingResult,
  state: BookState,
) => {
  const currentIndex = state.exercise?.currentExercise?.currentSlide || 0;
  // @ts-ignore
  const dataTraining = (state.exercise?.currentExercise?.data || []).filter((el: {
    required: boolean;
  }) => el.required);

  if (dataTraining[currentIndex]) {
    const courseElement = `/api/course/elements/${dataTraining[currentIndex].id}`;
    const session = `/api/course/session/course_sessions/${state.route?.sessionId}`;
    const resultElement = { courseElement, session, ...result };
    const resultAnswer = { ...resultElement, isCorrect: result.isCorrect };

    // @ts-ignore
    dataTraining[currentIndex].elements[0].elementAnswers[0] = {
      // @ts-ignore
      ...(dataTraining?.[currentIndex]?.elements?.[0]?.elementAnswers?.[0] || {}),
      ...resultAnswer,
    };

    return {
      // @ts-ignore
      id: dataTraining?.[currentIndex]?.elements?.[0]?.elementAnswers?.[0]?.id || null,
      resultElement,
      state: {
        ...state,
        exercise: {
          ...state.exercise,
          currentExercise: {
            ...state.exercise.currentExercise,
            answers: resultAnswer,
            data: dataTraining,
          },
        },
      },
    };
  }

  return {
    state,
  };
};

const convertExerciseResult = (
  state: BookState,
) => ({
  id: state.exercise.currentExercise.exerciseProgress?.[0]?.id || null,
  result: {
    exercise: `/api/course/exercises/${state.exercise.currentExercise.id}`,
    session: `/api/course/session/course_sessions/${state.route.sessionId}`,
    testInfo: { total: null, right: null, wrong: null },
  },
});

const convertReviewResult = (
  result?: TrainingResult,
  state?: BookState,
) => {
  const updated = { ...state };
  const index = findIndex(
    // @ts-ignore
    (state.exercise.currentExercise.data as ExercisesHWReviewElement[]) || [],
    (el) => el.alias === 'write-it-up',
  );
  let resultElement: any = {};

  if (
    index === -1
    // @ts-ignore
    || !updated.exercise.currentExercise.data[index].elements[0].elementAnswers[0]?.mistake
  ) {
    //
  } else {
    resultElement = {
      // @ts-ignore
      id: updated.exercise.currentExercise.data[index].elements[0].elementAnswers[0].id,
      result: {
        // @ts-ignore
        answer: updated.exercise.currentExercise.data[index].elements[0].elementAnswers[0].answer,
        isCorrect: true,
        mistake:
          // @ts-ignore
          updated.exercise.currentExercise.data[index].elements[0].elementAnswers[0].mistake,
        trainingType: 'WriteItUp',
      },
    };
  }

  if (result?.answer) {
    // @ts-ignore
    updated.exercise.currentExercise.data[index].elements[0].elementAnswers[0].mistake = [
      result.answer,
    ];
  }

  return {
    // @ts-ignore
    id: state.exercise.currentExercise.exerciseProgress?.[0]?.id || null,
    state: updated,
    result: {
      exercise: `/api/course/exercises/${state.exercise.currentExercise.id}`,
      session: `/api/course/session/course_sessions/${state.route.sessionId}`,
      testInfo: { total: null, right: null, wrong: null },
    },
    resultElement,
  };
};

const convertTestResult = (
  result: TrainingResult,
  state: BookState,
  btnType: string,
) => {
  const { exercise: { currentExercise: { exerciseProgress = [] } } } = state;
  const dataTraining = state.exercise?.currentExercise?.dataTraining;
  const prevIsCorrect = (exerciseProgress?.[0]?.testInfo?.wrong || 5) !== 5;

  let mistakes: number = exerciseProgress?.[0]?.testProgress?.testInfo?.wrong || 0;
  let correct: number = exerciseProgress?.[0]?.testProgress?.testInfo?.right || 0;

  if (result.isCorrect) {
    correct += 1;
  } else if (
    state.exercise.currentExercise.lastSlideAnswer
    !== state.exercise.currentExercise.currentSlide
  ) {
    mistakes += 1;
  }

  const testProgress = {
    exercise: `/api/course/exercises/${state.exercise.currentExercise.id}`,
    session: `/api/course/session/course_sessions/${state.route?.sessionId}`,
    testInfo: {
      total: dataTraining.length,
      right: correct,
      wrong: mistakes,
    },
  };

  return {
    id: exerciseProgress?.[0]?.id || null,
    needSendResult:
      (mistakes > 4 || (mistakes + correct === dataTraining.length)) && !prevIsCorrect,
    testProgress,
    state: {
      ...state,
      exercise: {
        ...state.exercise,
        currentExercise: {
          ...state.exercise.currentExercise,
          answers: {
            ...state.exercise.currentExercise.answers,
            isCorrect: result.isCorrect,
          },
          exerciseProgress: [{
            ...(exerciseProgress[0] || {}),
            testProgress,
          }],
          isFinishedTraining: btnType !== 'CHECK_CLICK',
          isFinishedExercise: testProgress.testInfo.wrong > 4,
          lastSlideAnswer: state.exercise.currentExercise.currentSlide,
        },
      },
    },
  };
};

const addTrainingAnswerId = (params: {
  id: number;
  state: BookState;
  trainingType: string;
}) => {
  const { currentExercise } = params.state.exercise;

  currentExercise.dataTraining[currentExercise.currentSlide].elementAnswers.forEach((el: {
    trainingType: string;
  }, key: number) => {
    if (el.trainingType === params.trainingType) {
      currentExercise.dataTraining[currentExercise.currentSlide].elementAnswers[key].id = params.id;
    }
  });

  return {
    ...params.state,
    exercise: {
      ...params.state.exercise,
      currentExercise,
    },
  };
};

const addExerciseProgress = (state: BookState, id: number) => {
  const {
    exercise: { currentExercise, exercises },
    lesson: { currentId },
  } = state;

  const activityKeys = ['lesson', 'homework'];

  activityKeys.forEach((activity: string) => {
    const { optional, required } = exercises[currentId][activity];

    required.concat(optional).forEach((el: ExerciseResponse, key: number) => {
      if (el.id === id && !el.exerciseProgress.length) {
        const type = el.required ? 'required' : 'optional';

        exercises[currentId][activity][type][key].exerciseProgress[0] = {
          id,
          __typename: 'Progress',
        };
      }
    });
  });

  if (currentExercise.id === id) {
    currentExercise.exerciseProgress[0] = { id, __typename: 'Progress' };
  }

  return {
    currentExercise,
    exercises,
  };
};

const addLessonProgress = (state: BookState, id: number) => {
  const {
    lesson: { lessons },
    route: { courseId },
  } = state;

  (lessons[courseId] || []).forEach((el: Lesson, key: number) => {
    if (el.id === id && !el.lessonProgress.length) {
      lessons[courseId][key].lessonProgress.push({
        id,
        __typename: 'Progress',
      });
    }
  });

  return lessons;
};

const removeCompleted = (data: Training[]) => {
  const updatedData: Training[] = [];

  data.forEach((el: {
    allowedTrainingTypes: string[];
    elementAnswers: {
      isCorrect: boolean;
      trainingType: string;
    }[];
  }) => {
    const allowedTrainingTypes : string[] = [];

    el.allowedTrainingTypes.forEach((type: string) => {
      if (!el.elementAnswers.filter((answer: {
        isCorrect: boolean;
        trainingType: string;
      }) => answer.trainingType === type && answer.isCorrect).length) {
        allowedTrainingTypes.push(type);
      }
    });

    // @ts-ignore
    updatedData.push({ ...el, allowedTrainingTypes });
  });

  return updatedData.filter((el: {
    allowedTrainingTypes: string[];
  }) => el.allowedTrainingTypes.length);
};

const removeSingleCompleted = (data: Training[]) => {
  const updatedData : Training[] = [];

  data.forEach((el: {
    allowedTrainingTypes: string[];
    elementAnswers: {
      isCorrect: boolean;
      trainingType: string;
    }[];
  }) => {
    let { allowedTrainingTypes } = el;

    if (el.elementAnswers.filter((answer: {
      isCorrect: boolean;
    }) => answer.isCorrect).length) {
      allowedTrainingTypes = [];
    }

    // @ts-ignore
    updatedData.push({ ...el, allowedTrainingTypes });
  });

  return updatedData.filter((el: {
    allowedTrainingTypes: string[];
  }) => el.allowedTrainingTypes.length);
};

const createShuffled = (
  exercise: { data: any[]; },
  correct: string,
  key: string,
  type?: string,
) => {
  const shuffled = shuffle(exercise.data);
  const parsed = [correct];

  shuffled.forEach((el: Training) => {
    if (el[key] !== correct) {
      if (type && el.type === type) {
        parsed.push(el[key]);
      } else if (!type) {
        parsed.push(el[key]);
      }
    }
  });

  return shuffle(parsed.splice(0, 3));
};

export {
  checkIsNeedReset,
  findLessonId,
  checkIsCanSave,
  checkIsNeedHWReview,
  getHomeworkIds,
  setHomeworkElements,
  convertTrainingResult,
  convertHWResult,
  convertExerciseResult,
  convertReviewResult,
  convertTestResult,
  addTrainingAnswerId,
  addExerciseProgress,
  addLessonProgress,
  removeCompleted,
  removeSingleCompleted,
  createShuffled,
};
